/*eslint-disable camelcase*/
import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';

import Strengths from '../../../assets/Brain.png';
import DiscussionLogs from '../../../assets/friend.png';
import MetricReview from '../../../assets/graph.png';
import { CoreValueDropdown } from '../../../components/coreValueDropdown/CoreValueDropdown';
import DescriptionBox from '../../../components/descriptionBox/DescriptionBox';
import { selectCurrentEmpid, selectCurrentUser } from '../../../pages/login/authSlice';
import { CoachingCategoryDropdown } from '../../../components/coachingCategoryDropdown/CoachingCategoryDropdown';
import {
    useGetCoachingMetricGoalQuery,
    useGetCoachingMonthlyMetricsQuery,
    useGetCoachingScorecardQuery,
    useInsertCoachingMetricsMutation,
    useInsertCoachingScorecardMutation,
} from '../metrics/metricsSlice';
import { useGetScorecardFilterByRoleQuery } from '../../../components/scoreCardFilter/scoreCardFilterSlice';
import { CoachingEntityTypes } from '../../../utils/typeConstants';
import { useCreateCAPRecordMutation } from '../conversationLogs/conversationLogSlice';
import { useGetCAPDataQuery } from '../coachingSlice';
import { changeDateFormate } from '../../../utils/dateFormat';
import './CAPCreationDialog.scss';
import MetricCreation from './MetricCreation';
import { useGetAIRecordingCAPMutation } from '../../../components/vertexAI/vertexAISlice';
import { LoadingSpinner } from '../../../components/loadingSpinner/loadingspinner';

interface CAPCreationDialogProps {
    visible: boolean;
    onClose: () => void;
    recipientId: number;
    coachingEntityId: number;
    readonly: boolean;
    date: Date;
    entityId?: number;
    showCAPDialog: Dispatch<React.SetStateAction<boolean>>;
    recordingFileName?: string;
}

const CAPCreationDialog = (props: CAPCreationDialogProps) => {
    const [insertCoachingScorecard] = useInsertCoachingScorecardMutation();
    const [insertCoachingMetrics] = useInsertCoachingMetricsMutation();
    const [saveCoachingRecord] = useCreateCAPRecordMutation();
    const [AIRecordingCAP] = useGetAIRecordingCAPMutation();
    const date = props.date ? new Date(props.date) : new Date();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const [strengthCoreValue, setStrengthCoreValue] = useState(0);
    const [discussionCoreValue, setDiscussionCoreValue] = useState(0);
    const [strengthTitle, setStrengthTitle] = useState('');
    const [discussionSubject, setDiscussionSubject] = useState('');
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const loggedEmpUser = useSelector(selectCurrentUser);
    const [strengthDescription, setStrengthDescription] = useState('');
    const [discussionDescription, setDiscussionDescription] = useState('');
    const [discussionCategory, setDiscussionCategory] = useState(0);
    const [metrics, setMetrics] = useState<number[]>([]);
    const [scorecardId, setScorecardId] = useState<number>(0);
    const [scorecardVersionId, setScorecardVersionId] = useState<number>(0);
    const [coachingScorecardData, setCoachingScorecardData] = useState<any>([]);
    const [discussionCoreValueDesc, setDiscussionCoreValueDesc] = useState<string>('');
    const [discussionCategoryName, setDiscussionCategoryName] = useState<string>('');
    const [strengthCoreValueDesc, setStrengthCoreValueDesc] = useState<string>('');
    const [authorEmployeeName, setAuthorEmployeeName] = useState<string>('');
    const [coreValueName, setCoreValueName] = useState<string>('');
    const [aiResponse, setAIResponse] = useState<any>({});
    const [loader, setLoader] = useState<boolean>(false);
    const [aiResponseLoading, setAIResponseLoading] = useState<boolean>(false);
    const [goalMetricData, setGoalMetricData] = useState<any>([]);
    const metricCreationRef = useRef<any>();

    const { data: coachingMonthlyMetrics, isFetching: CoachingMonthlyMetricsFetching } =
        useGetCoachingMonthlyMetricsQuery(
            {
                employeeNumber: props?.recipientId,
                year,
                month,
                scorecardId,
                scorecardVersionId
            },
            { skip: !props?.recipientId || !year || !month || scorecardId === 0 }
        );

    const { data: scorecardFilterDataByRole, isFetching: scorecardFilterDataByRoleFetching } =
        useGetScorecardFilterByRoleQuery(
            {
                employeenumber: loggedEmpNum,
                applicationRoleId: 1,
                year,
                month,
            },
            {
                skip: !month || !year || !loggedEmpNum,
            }
        );

    const { data: coachingScorecardAPIData, isFetching: coachingScorecardAPIFetching } =
        useGetCoachingScorecardQuery(
            {
                employeeNumber: props?.recipientId,
                year,
                month,
                scorecardId,
                entityTypeId: CoachingEntityTypes.RecordingCAP,
                entityId: props?.entityId,
            },
            { skip: !props?.recipientId || !year || !month || scorecardId === 0 }
        );

    const { data: capData, isFetching: capDataFetching } = useGetCAPDataQuery(
        {
            employeeNumber: props?.recipientId,
            entityTypeId: CoachingEntityTypes.RecordingCAP,
            entityId: props?.coachingEntityId,
        },
        {
            skip: !props?.recipientId || !props?.coachingEntityId || !props?.readonly,
        }
    );

    const { data: coachingMetricGoal, isFetching: coachingMetricGoalFetching } =
        useGetCoachingMetricGoalQuery(
            {
                employeeNumber: props?.recipientId,
                coachingScorecardId: coachingScorecardData?.data?.[0]?.coaching_scorecard_id,
            },
            { skip: !props?.recipientId || !coachingScorecardData?.data?.[0]?.coaching_scorecard_id }
        );

    useEffect(() => {
        if (scorecardFilterDataByRole?.data?.length === 0 || coachingMonthlyMetrics?.data?.length === 0) {
            props?.showCAPDialog(false);
            toast.warn(
                'There is no Scorecard data calculated for the current month.  This option will become available for this recording once the Scorecard has been calculated for the current month.'
            );
        }
    }, [scorecardFilterDataByRole, coachingMonthlyMetrics]);

    useEffect(() => {
        if (
            coachingMetricGoalFetching ||
            CoachingMonthlyMetricsFetching ||
            scorecardFilterDataByRoleFetching ||
            coachingScorecardAPIFetching ||
            capDataFetching ||
            aiResponseLoading
        ) {
            setLoader(true);
        } else setLoader(false);
    }, [
        coachingMetricGoalFetching,
        CoachingMonthlyMetricsFetching,
        scorecardFilterDataByRoleFetching,
        coachingScorecardAPIFetching,
        capDataFetching,
        aiResponseLoading,
    ]);

    useEffect(() => {
        if (props?.readonly === false) {
            if (coachingMonthlyMetrics?.data?.length > 0) {
                const array: any = coachingMonthlyMetrics?.data[0].metrics.map((item: any) => {
                    return item.label;
                });
                if (!props?.readonly && props?.recordingFileName) {
                    AIRecordingCAPRequest(props?.recordingFileName, array);
                }
            }
        }
    }, [props?.readonly, coachingMonthlyMetrics]);

    useEffect(() => {
        if (scorecardFilterDataByRole?.data && scorecardFilterDataByRole?.data?.length > 0){
            setScorecardId(scorecardFilterDataByRole?.data[0]?.scorecard_id);
        setScorecardVersionId(scorecardFilterDataByRole?.data[0]?.scorecard_version_id);}

        else {
            setScorecardId(0);
            setScorecardVersionId(0);
        }
    }, [scorecardFilterDataByRole]);

    useEffect(() => {
        if (props?.readonly === false) {
            if (coachingScorecardData && coachingScorecardData?.data?.length === 0 && metrics.length > 0) {
                const coachingScorecardData = {
                    employeeNumber: props?.recipientId,
                    authorEmployeeNumber: loggedEmpNum,
                    scorecardId,
                    year,
                    month,
                    goalEmployeeNumber: props?.recipientId,
                    metricIds: metrics,
                    entityTypeId: CoachingEntityTypes.RecordingCAP,
                    entityId: props?.entityId,
                };
                InsertCoachingScorecardData(coachingScorecardData);
            } else if (coachingScorecardData?.data?.length > 0 && metrics.length > 0) {
                const count = coachingScorecardData?.data[0]?.coaching_metric_ids
                ?.split(',')
                .map((id: any) => id.trim())
                .filter((id: any) => id !== '').length;
                if (count !== metrics.length) {
                    InsertCoachingMetrics(coachingScorecardData?.data[0]?.coaching_scorecard_id);
                }
            }
        }
    }, [coachingScorecardData, metrics]);

    useEffect(() => {
        if (coachingMonthlyMetrics?.data?.length > 0) {
            const array: any = coachingMonthlyMetrics?.data[0].metrics.map((item: any) => {
                return item.metric_id;
            });
            setMetrics(array);
        }
    }, [coachingMonthlyMetrics]);

    useEffect(() => {
        setCoachingScorecardData(coachingScorecardAPIData);
    }, [coachingScorecardAPIData]);

    useEffect(() => {
        if (capData?.data?.length > 0) {
            const capDetails = capData?.data[0];
            setStrengthTitle(capDetails?.strength_title);
            setStrengthDescription(capDetails?.strength_description);
            setStrengthCoreValue(capDetails?.strength_core_value_id);
            setStrengthCoreValueDesc(capDetails?.strength_core_value);
            setDiscussionSubject(capDetails?.discussion_subject);
            setDiscussionDescription(capDetails?.discussion_description);
            setDiscussionCoreValueDesc(capDetails?.discussion_core_value);
            setDiscussionCoreValue(capDetails?.discussion_core_value_id);
            setDiscussionCategory(capDetails?.discussion_category_id);
            setDiscussionCategoryName(capDetails?.discussion_category_name);
            setAuthorEmployeeName(capDetails?.author_employee_name);
        } else {
            setDiscussionCategory(11); //Default to Coaching Conversation category
        }
    }, [capData]);

    useEffect(() => {
        if (aiResponse && Object.keys(aiResponse).length > 0 && props?.readonly === false) {
            //strength values
            const strengthTitle = aiResponse?.strengths?.title || '';
            const strengthDescription = aiResponse?.strengths?.description || '';
            const coreValue = aiResponse?.ttecValue || '';
            //discussion values
            const discussionDesc = aiResponse?.discussion?.content || '';
            const discussionSubject = aiResponse?.discussion?.subject || '';
            //metric values

            if (strengthTitle && strengthTitle?.length > 0) setStrengthTitle(strengthTitle);
            if (strengthDescription && strengthDescription?.length > 0)
                setStrengthDescription(strengthDescription);

            if (discussionDesc && discussionDesc?.length > 0) setDiscussionDescription(discussionDesc);
            if (discussionSubject && discussionSubject?.length > 0) setDiscussionSubject(discussionSubject);

            if (coreValue && coreValue?.length > 0) setCoreValueName(coreValue);
        }
    }, [aiResponse]);

    const AIRecordingCAPRequest = (fileName: string | null, metricNames: string[]) => {
        const data = {
            fileName,
            maxLength: 3900,
            metrics: metricNames,
            employeeNumber: props?.recipientId.toString(),
        };
        setAIResponseLoading(true);
        AIRecordingCAP(data)
        .unwrap()
        .then((res: any) => {
            if (res) {
                setAIResponse(extractPerformanceFieldsFromJson(res?.response?.replace(/(##|\*\*)/g, '')));
            }
            setAIResponseLoading(false);
        })
        .then((error: any) => {
            console.log(error);
        });
    };

    function extractPerformanceFieldsFromJson(jsonString: string) {
        // Clean the JSON string by removing markdown code blocks
        const cleanedJson = cleanJsonString(jsonString);
        const data = JSON.parse(cleanedJson);
        return data;
    }

    function cleanJsonString(jsonString: string): string {
        let cleaned = jsonString.trim();
        if (cleaned.startsWith('```json')) {
            cleaned = cleaned.replace(/^```json\s*/, '').replace(/\s*```$/, '');
        }
        if (cleaned.startsWith('```')) {
            cleaned = cleaned.replace(/^```\s*/, '').replace(/\s*```$/, '');
        }
        if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
            cleaned = cleaned.slice(1, -1);
        }

        return cleaned.trim();
    }
    const extractMetricsFromAIResponse = (aiResponse: any) => {
        try {
            const metrics = aiResponse?.metrics;
            const metricsArray = Object.keys(metrics)
            .map((key) => ({
                name: metrics[key].name,
                description: metrics[key].description,
                skill: metrics[key].skill,
                behavior: metrics[key].behavior,
                coreValue: metrics[key].coreValue,
                discussed: metrics[key].discussed,
            }))
            .filter((metric) => metric.name);

            return metricsArray;
        } catch (error) {
            console.error('Error parsing AI response:', error);
            return [];
        }
    };

    const mapAIDataToMetricGoals = (goalMetricData: any[], aiResponse: string) => {
        const aiMetrics = extractMetricsFromAIResponse(aiResponse);

        return goalMetricData.map((goalItem) => {
            const aiMetric = aiMetrics.find((ai) => ai.name === goalItem.metric_name);

            if (aiMetric) {
                return {
                    ...goalItem,
                    author_comment: aiMetric.description || goalItem.goal_comment || '',
                    skill_name: aiMetric.skill,
                    behavior_name: aiMetric.behavior,
                    core_value_name: aiMetric.coreValue,
                };
            }

            return goalItem;
        });
    };

    useEffect(() => {
        if (coachingMetricGoal?.data?.length > 0 && coachingMonthlyMetrics?.data?.[0]?.metrics) {
            const orderedGoalData = coachingMonthlyMetrics.data[0].metrics
            .map((metricItem: any) => {
                return coachingMetricGoal.data.find((goal: any) => goal.metric_id === metricItem.metric_id);
            })
            .filter(Boolean);
            if (props?.readonly) setGoalMetricData(orderedGoalData);
            if (aiResponse && Object.keys(aiResponse).length > 0 && props?.readonly === false) {
                const mappedGoalData = mapAIDataToMetricGoals(orderedGoalData, aiResponse);
                setGoalMetricData(mappedGoalData);
            }
        }
    }, [coachingMetricGoal, coachingMonthlyMetrics, aiResponse]);

    const InsertCoachingMetrics = (coachingScorecardId: number) => {
        const coachingMetricsData = {
            coachingScorecardId,
            metricIds: metrics,
            authorEmployeeNumber: loggedEmpNum,
        };

        insertCoachingMetrics(coachingMetricsData)
        .unwrap()
        .then((data: any) => {
            if (data?.data) {
                return true;
            }
            return toast.error('Create Coaching Metrics failed');
        });
    };

    const InsertCoachingScorecardData = (coachingScorecardData: any) => {
        insertCoachingScorecard(coachingScorecardData)
        .unwrap()
        .then((data: any) => {
            if (data?.data) {
                return true;
            }
            return toast.error('Create Coaching Scorecard failed');
        });
    };
    const renderHeader = () => {
        return (
            <div>
                <h3>Coaching Action Plan</h3>
            </div>
        );
    };
    const ValidateDiscussion = () => {
        const isFormEmpty =
            (!discussionSubject || discussionSubject?.trim().length === 0) &&
            (!discussionCoreValue || discussionCoreValue === 0) &&
            (!discussionDescription || discussionDescription?.trim().length === 0) &&
            (discussionCategory === 0 || discussionCategory === null || discussionCategory === undefined);

        if (isFormEmpty) {
            return true;
        }
        const errors: string[] = [];

        if (props.recipientId === null) {
            errors.push('Recipient is required \n');
        }
        if (discussionSubject?.trim().length === 0) {
            errors.push('Subject is required \n');
        }
        if (discussionSubject?.trim().length > 100) {
            errors.push('Subject must be less than 100 characters \n');
        }
        if (!discussionCoreValue || discussionCoreValue === 0) {
            errors.push('TTEC  Corevalue is required \n');
        }
        if (discussionDescription?.trim().length < 10) {
            errors.push('Description is required and must be at least 10 characters \n');
        }
        if (discussionDescription?.trim().length > 4000) {
            errors.push('Description must be less than 4000 characters \n');
        }
        if (discussionCategory === 0) {
            errors.push('Discussion category is required \n');
        }

        if (errors.length > 0) {
            const ErrorComponent = (
                <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        Please fix the errors in Discussion:
                    </div>
                    {errors.map((error, index) => (
                        <div key={index}>- {error}</div>
                    ))}
                </div>
            );

            toast.error(ErrorComponent);
            return false;
        }
        return true;
    };
    const validateStrength = () => {
        const isFormEmpty =
            (!strengthTitle || strengthTitle.trim().length === 0) &&
            (!strengthCoreValue || strengthCoreValue === 0) &&
            (!strengthDescription || strengthDescription.trim().length === 0);

        // If form is completely empty, consider it valid
        if (isFormEmpty) {
            return true;
        }
        const errors: string[] = [];
        if (strengthTitle?.trim().length === 0) {
            errors.push('Title is required \n');
        }

        if (!strengthCoreValue || strengthCoreValue === 0) {
            errors.push('TTEC value is required \n');
        }

        if (strengthDescription?.trim().length < 10) {
            errors.push('Description is required and must be at least 10 characters \n');
        }
        if (errors.length > 0) {
            const ErrorComponent = (
                <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        Please fix the errors in Strength:
                    </div>
                    {errors.map((error, index) => (
                        <div key={index}>- {error}</div>
                    ))}
                </div>
            );

            toast.error(ErrorComponent);
            return false;
        }
        return true;
    };
    const handleSave = () => {
        const payload: any = {};
        const validationSuccess = ValidateDiscussion() && validateStrength();
        if (validationSuccess) {
            payload.discussion = {
                empNo: props?.recipientId.toString(),
                authorEmpNo: loggedEmpNum.toString(),
                coachingConversationCategoryId: String(discussionCategory),
                conversationDate: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
                subject: discussionSubject,
                conversationDescription: discussionDescription,
                coreValueId: String(discussionCoreValue),
                visibleToRecipient: true,
                coachingEntityTypeId: CoachingEntityTypes.RecordingCAP,
                coachingEntityId: props.entityId ? String(props.entityId) : undefined,
            };
            payload.strengths = [
                {
                    employeeNumber: props?.recipientId.toString(),
                    authorEmployeeNumber: loggedEmpNum.toString(),
                    strengthDescription,
                    strengthTitle,
                    coreValueId: strengthCoreValue,
                    year,
                    month,
                    coachingEntityId: props.entityId ? String(props.entityId) : undefined,
                    coachingEntityTypeId: CoachingEntityTypes.RecordingCAP,
                },
            ];
            let latestMetricData = goalMetricData;

            // Only get latest data from ref if in create mode
            if (!props.readonly) {
                latestMetricData = metricCreationRef.current?.getLatestMetricData() || goalMetricData;
            }
            const metricRequests = latestMetricData?.filter(
                (metric: any) => metric?.author_comment && metric?.author_comment.trim().length > 0
            );
            if (metricRequests?.length > 0) {
                payload.metrics = metricRequests?.map((metric: any) => {
                    const currDate = new Date();
                    let authorCommentDateValue = metric?.author_comment_date;
                    if (!authorCommentDateValue || authorCommentDateValue === '') {
                        authorCommentDateValue = `${currDate.getFullYear()}-${
                            currDate.getMonth() + 1
                        }-${currDate.getDate()}`;
                    }
                    return {
                        ...metric,
                        authorCommentDate: authorCommentDateValue,
                        authorComment: metric?.author_comment,
                        coachingSkillId: metric?.coaching_skill_id === 0 ? null : metric?.coaching_skill_id,
                        coachingBehaviorId:
                            metric?.coaching_behavior_id === 0 ? null : metric?.coaching_behavior_id,
                        coreValueId: metric?.core_value_id === 0 ? null : metric?.core_value_id,

                        coachingMetricId: metric?.coaching_metric_id,
                    };
                });
            }
            saveCoachingRecord(payload)
            .then((res: any) => {
                if (res?.data?.data) {
                    toast.success('CAP created successfully');
                    props.showCAPDialog(false);
                } else {
                    toast.error('Failed to save CAP');
                    props.showCAPDialog(false);
                }
            })
            .catch((error: any) => {
                toast.error('Error saving CAP: ' + error.message);
            });
        }
    };
    return (
        <Dialog
            visible={props?.visible}
            onHide={props?.onClose}
            header={renderHeader()}
            style={{ width: '70%', borderRadius: '12px' }}
            modal
        >
            <div className="row">
                <div className="col-md-12">
                    <div className="me-2">
                        <h6>Date</h6>
                        <span>{changeDateFormate(date)}</span>
                    </div>
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-md-12">
                    <div className="strengths-tool">
                        <img src={Strengths} alt="Strengths" />{' '}
                        <span style={{ color: '#A9A5FF' }} className="header-text">
                            Strengths
                        </span>
                    </div>
                </div>
            </div>
            <Divider style={{ borderColor: '#A9A5FF' }} />
            <div
                style={{ border: '1px solid #D3D3D3', borderRadius: '20px', padding: '20px', margin: '15px' }}
            >
                {props?.readonly && (strengthCoreValue === 0 || strengthCoreValue === null) ? (
                    <div className="text-center mt-4 mb-4 no-data-label" style={{ fontSize: '25px' }}>
                        No Data Available
                    </div>
                ) : (
                    <>
                        <div className="row">
                            <div className="col-md-4">
                                <h6>Title</h6>
                                <div>
                                    {props.readonly ? (
                                        <span>{strengthTitle}</span>
                                    ) : (
                                        <InputText
                                            data-testid="subject-input"
                                            placeholder="Title"
                                            className="w-100 borderRadius8"
                                            value={strengthTitle}
                                            onChange={(e) => setStrengthTitle(e.target.value)}
                                            disabled={props.readonly}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="col-md-4">
                                {props.readonly ? (
                                    <>
                                        <h6>TTEC Value</h6>
                                        <div>
                                            <span>{strengthCoreValueDesc}</span>
                                        </div>
                                    </>
                                ) : (
                                    <CoreValueDropdown
                                        fullWidth
                                        coreValue={strengthCoreValue}
                                        setCoreValue={setStrengthCoreValue}
                                        coreValueName={coreValueName}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12 mt-4">
                                <DescriptionBox
                                    createdByLabel="Author"
                                    createdByValue={props.readonly ? authorEmployeeName : loggedEmpUser}
                                    title={'Comment'}
                                    responseDescription={strengthDescription}
                                    value={strengthDescription}
                                    limit={4000}
                                    onChange={setStrengthDescription}
                                    readOnly={props.readonly}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="row mt-4">
                <div className="metric-tool">
                    <img src={MetricReview} alt="Metric Review" />{' '}
                    <span style={{ color: '#F93' }} className="header-text">
                        Metric Review
                    </span>
                </div>
            </div>
            <Divider />
            <div
                style={{ border: '1px solid #D3D3D3', borderRadius: '20px', padding: '20px', margin: '15px' }}
            >
                <div className="row">
                    {coachingMonthlyMetrics?.data?.length > 0 ? (
                        // <>
                        <div className="col-md-12">
                            <MetricCreation
                                ref={!props.readonly ? metricCreationRef : null}
                                coachingMetric={coachingMonthlyMetrics}
                                goalMetricData={goalMetricData}
                                //setGoalMetricData={setGoalMetricData}
                                readOnly={props.readonly}
                            />
                        </div>
                    ) : (
                        <div className="text-center mt-4 mb-4 no-data-label" style={{ fontSize: '25px' }}>
                            No Data Available
                        </div>
                    )}
                </div>
            </div>
            <div className="row mt-4">
                <div className="discussion-tool">
                    <img src={DiscussionLogs} alt="Discussion Logs" />
                    <span style={{ color: '#3299FF' }} className="header-text">
                        Discussions
                    </span>
                </div>
            </div>

            <Divider />
            <div
                style={{ border: '1px solid #D3D3D3', borderRadius: '20px', padding: '20px', margin: '15px' }}
            >
                {props?.readonly && (!discussionCategory || discussionCategory === 0) ? (
                    <div className="text-center mt-4 mb-4 no-data-label" style={{ fontSize: '25px' }}>
                        No Data Available
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-4">
                            <h6>Subject</h6>
                            <div>
                                {props.readonly ? (
                                    <span>{discussionSubject}</span>
                                ) : (
                                    <InputText
                                        placeholder="Input Subject"
                                        className="w-100 borderRadius8"
                                        value={discussionSubject}
                                        onChange={(e) => setDiscussionSubject(e.target.value)}
                                        disabled={props.readonly}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="col-md-4">
                            {props.readonly ? (
                                <>
                                    <h6>TTEC Value</h6>
                                    <div>
                                        <span>{discussionCoreValueDesc}</span>
                                    </div>
                                </>
                            ) : (
                                <CoreValueDropdown
                                    fullWidth
                                    coreValue={discussionCoreValue}
                                    setCoreValue={setDiscussionCoreValue}
                                    coreValueName={coreValueName}
                                />
                            )}
                        </div>
                        <div className="col-md-4">
                            {props.readonly ? (
                                <>
                                    <h6>Category</h6>
                                    <div>
                                        <span>{discussionCategoryName}</span>
                                    </div>
                                </>
                            ) : (
                                <CoachingCategoryDropdown
                                    data-testid="category-dropdown"
                                    fullWidth
                                    category={discussionCategory}
                                    setCategory={setDiscussionCategory}
                                />
                            )}
                        </div>
                        <div className="col-md-12 mt-4">
                            <DescriptionBox
                                createdByLabel="Author"
                                createdByValue={props.readonly ? authorEmployeeName : loggedEmpUser}
                                title={'Comment'}
                                responseDescription={discussionDescription}
                                value={discussionDescription}
                                limit={4000}
                                onChange={setDiscussionDescription}
                                readOnly={props.readonly}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="d-flex justify-content-end mt-2">
                {!props.readonly && (
                    <div className="mt-2 mb-2">
                        <Button
                            label="Cancel"
                            onClick={() => props.showCAPDialog(false)}
                            className="cancelButton me-2 p-2 btn-secondary"
                        />
                        <Button
                            label="Save"
                            onClick={handleSave}
                            className="saveButton me-2 p-2 btn-primary"
                        />
                    </div>
                )}
            </div>
            <div>{loader ? <LoadingSpinner /> : ''}</div>
        </Dialog>
    );
};

export default CAPCreationDialog;
